// frontend/modules/action-flow/shared/utils/formatters.ts
/**
 * Formats a date string or timestamp into a localized date and time string
 *
 * @param dateString - Date string, timestamp, or Date object
 * @param options - Intl.DateTimeFormatOptions to customize output
 * @returns Formatted date and time string
 */
export const formatDateTime = (
    dateString?: string | number | Date | null,
    options: Intl.DateTimeFormatOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    }
): string => {
    if (!dateString) return 'N/A';

    try {
        const date = typeof dateString === 'object'
            ? dateString
            : new Date(dateString);

        return new Intl.DateTimeFormat('en-US', options).format(date);
    } catch (error) {
        console.error('Error formatting date:', error);
        return 'Invalid date';
    }
};

/**
 * Formats a date string or timestamp into a relative time string (e.g., "2 hours ago")
 *
 * @param dateString - Date string, timestamp, or Date object
 * @returns Relative time string
 */
export const formatRelativeTime = (dateString?: string | number | Date | null): string => {
    if (!dateString) return 'N/A';

    try {
        const date = typeof dateString === 'object'
            ? dateString
            : new Date(dateString);

        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return 'just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`;

        return formatDateTime(date, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (error) {
        console.error('Error formatting relative time:', error);
        return 'Invalid date';
    }
};